import React from "react";
import PropTypes from "prop-types";

import Icon from "../Icon";

// The library as a table, grouped by channel — "here are French channels you might like",
// with what we hold for each underneath.
//
// A different question from the player view, not a different skin on it. The player
// answers "what should I watch now"; this answers "whose channel is worth following", which
// is what someone building a habit actually wants. Filters apply to the clips, and a
// channel appears only if something of its survived them — so filtering by subjonctif
// shows the channels that use it.
//
// Links out to YouTube deliberately: this is a catalogue of other people's work, and a
// catalogue that won't let you go to the source is worse for everyone, us included.
const formatCount = (n) => {
  if (!n) return null;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}K`;
  return `${n}`;
};

const ChannelTable = ({ channels: meta, clips, formatDuration, onPlay, selectedId, sentenceCase }) => {
  const channels = [];
  const byId = new Map();

  clips.forEach((clip) => {
    const id = clip.channel_external_id || clip.channel_title || "unknown";

    if (!byId.has(id)) {
      const channel = {
        categories: new Set(),
        clips: [],
        id,
        subscribers: (meta[id] || {}).subscribers,
        title: clip.channel_title || "Unknown channel",
      };
      byId.set(id, channel);
      channels.push(channel);
    }

    byId.get(id).clips.push(clip);

    // A set rather than a single value: discovery assigns one category per channel today,
    // but a channel can be returned under several, and showing only the first would be
    // quietly wrong the moment that happens.
    if (clip.ai_category) byId.get(id).categories.add(clip.ai_category);
  });

  // Subscribers first, clip count as the tiebreak. Clip count alone was fine when depth
  // varied, but on a discovery pass every channel holds roughly the same few clips, so it
  // sorts nothing — audience size is what actually distinguishes them. Channels with no
  // captured subscriber count fall to the bottom rather than the top.
  channels.sort((a, b) =>
    (b.subscribers || 0) - (a.subscribers || 0) || b.clips.length - a.clips.length
  );

  return (
    <div className="media-clips-table">
      {channels.map((channel) => (
        <section className="media-clips-channel" key={channel.id}>
          <h3 className="media-clips-channel-heading">
            {/* The icon is doing real work here: a channel name styled as a heading reads
                as a label, not a link, so without it nobody discovers it's clickable. */}
            <a
              href={`https://www.youtube.com/channel/${channel.id}/shorts`}
              rel="noopener noreferrer"
              target="_blank"
            >
              {channel.title} <Icon name="new-window" />
            </a>
            <span className="media-clips-count">
              {channel.clips.length} {channel.clips.length === 1 ? "clip" : "clips"}
              {channel.subscribers ? ` · ${formatCount(channel.subscribers)} subs` : ""}
            </span>

            {[...channel.categories].map((name) => (
              <span className="media-clips-channel-tag" key={name}>
                {name}
              </span>
            ))}
          </h3>

          <table className="table">
            <tbody>
              {channel.clips.map((clip) => (
                <tr
                  className={selectedId === clip.external_id ? "is-active" : ""}
                  key={clip.external_id}
                >
                  <td className="media-clips-cell-level">
                    {clip.cefr_level && clip.cefr_level.toUpperCase()}
                  </td>
                  <td className="media-clips-cell-title">
                    {clip.title}
                    {(clip.grammar_tags || []).length > 0 && (
                      <span className="media-clips-descriptor">
                        {clip.grammar_tags.map(sentenceCase).join(", ")}
                      </span>
                    )}
                  </td>
                  <td className="media-clips-cell-meta">{formatDuration(clip.duration_seconds)}</td>
                  <td className="media-clips-cell-meta">
                    {clip.words_per_minute && `${Math.round(clip.words_per_minute)} wpm`}
                  </td>
                  <td className="media-clips-cell-actions">
                    <button onClick={() => onPlay(clip)} type="button">
                      ▶ Play
                    </button>
                    <a
                      href={`https://www.youtube.com/shorts/${clip.external_id}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      YouTube <Icon name="new-window" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
};

ChannelTable.propTypes = {
  channels: PropTypes.object,
  clips: PropTypes.arrayOf(PropTypes.object).isRequired,
  formatDuration: PropTypes.func.isRequired,
  onPlay: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
  sentenceCase: PropTypes.func.isRequired,
};

ChannelTable.defaultProps = {
  channels: {},
  selectedId: null,
};

export default ChannelTable;
